import { useWorkspaceText, type WorkspaceText } from '../interface-copy';
import { Fragment, useMemo, type ReactNode } from 'react';
import { WorkspaceMarkdown, type MarkRenderer } from '../markdown';
import { sectionAnchor } from './format';
import { codePointOffsets, planChunks, type Chunk, type ChunkPlan, type MarkInput } from './marks';
import type { PageSection } from './types';

/** How the page shows a suggestion's marks: its number, and whether it is the one in focus. */
function markRenderer(
  focused: number | null,
  onMarker: ((index: number) => void) | null,
  uiText: WorkspaceText,
): MarkRenderer {
  return {
    mark: (kind, index, content, key) => {
      const Tag = kind === 'del' ? 'del' : 'ins';
      return (
        <Tag
          key={key}
          className={`ws-suggest-${kind}${index === focused ? ' is-focused' : ''}`}
          data-suggestion-mark={index}
        >
          <span className="ws-sr-only">
            {kind === 'del' ? uiText('Removed:') : uiText('Added:')}{' '}
          </span>
          {content}
        </Tag>
      );
    },
    marker: (index, key) =>
      onMarker ? (
        <button
          key={key}
          type="button"
          className={`ws-suggest-marker${index === focused ? ' is-focused' : ''}`}
          data-suggestion-marker={index}
          aria-label={uiText('Suggestion {value1}: show its card', { value1: index + 1 })}
          onClick={() => onMarker(index)}
        >
          {index + 1}
        </button>
      ) : null,
    block: (kind, index, content, key) => (
      <div
        key={key}
        className={`ws-suggest-block ws-suggest-block-${kind}${index === focused ? ' is-focused' : ''}`}
      >
        {content}
      </div>
    ),
  };
}

function BlockParts({
  chunk,
  focused,
  onMarker,
}: {
  chunk: Extract<Chunk, { kind: 'blocks' }>;
  focused: number | null;
  onMarker: ((index: number) => void) | null;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const inFocus = focused !== null && chunk.indexes.includes(focused);
  return (
    <>
      {chunk.parts.map((part, position) =>
        part.kind === 'equal' ? (
          <WorkspaceMarkdown key={position} text={part.text} />
        ) : (
          <div
            key={position}
            className={`ws-suggest-block ws-suggest-block-${part.kind === 'remove' ? 'del' : 'ins'}${inFocus ? ' is-focused' : ''}`}
            data-suggestion-mark={chunk.indexes[0]}
            data-suggestion-marks={chunk.indexes.join(' ')}
          >
            <span className="ws-sr-only">
              {part.kind === 'remove' ? uiText('Removed:') : uiText('Added:')}
            </span>
            <WorkspaceMarkdown text={part.text} />
          </div>
        ),
      )}
      {onMarker && (
        <p className="ws-suggest-block-markers">
          {chunk.indexes.map((index) => (
            <button
              key={index}
              type="button"
              className={`ws-suggest-marker${index === focused ? ' is-focused' : ''}`}
              data-suggestion-marker={index}
              aria-label={uiText('Suggestion {value1}: show its card', { value1: index + 1 })}
              onClick={() => onMarker(index)}
            >
              {index + 1}
            </button>
          ))}
        </p>
      )}
    </>
  );
}

/**
 * A page's text with tracked changes (or without, as everyone reads it). The
 * text is split at section starts, so a section link can focus its section,
 * and around each changed passage, so a docked page can put a suggestion's
 * card right under it (`after`).
 */
/** The page's chunks for `marks` (none to read it as everyone does). */
export function usePagePlan(
  markdown: string,
  sections: PageSection[],
  marks: MarkInput[],
  showMarkers = true,
): ChunkPlan {
  return useMemo(() => {
    const unit = codePointOffsets(markdown);
    const starts = sections
      .map((section) => ({ id: section.id, start: unit(section.start) }))
      .filter((section): section is { id: string; start: number } => section.start !== null);
    return planChunks(markdown, marks, starts, { markers: showMarkers });
  }, [markdown, sections, marks, showMarkers]);
}

export function MarkedPage({
  plan,
  focused = null,
  onMarker = null,
  after,
  focusSection,
  showMarkers = true,
  label,
}: {
  plan: ChunkPlan;
  focused?: number | null;
  onMarker?: ((index: number) => void) | null;
  /** Content to show right after the chunk where each listed suggestion first appears. */
  after?: (indexes: number[]) => ReactNode;
  focusSection?: string;
  showMarkers?: boolean;
  label: string;
}) {
  const { text: uiText } = useWorkspaceText();
  const renderer = markRenderer(focused, showMarkers ? onMarker : null, uiText);
  const firstChunk = new Map<number, string>();
  for (const chunk of plan.chunks)
    if (chunk.kind !== 'plain')
      for (const index of chunk.indexes)
        if (!firstChunk.has(index)) firstChunk.set(index, chunk.key);
  return (
    <article className="ws-page-article" aria-label={label}>
      {plan.chunks.map((chunk) => {
        // The chunk carries its first section's anchor; any later section it
        // holds (inside one changed passage) gets an anchor at its top.
        const [first, ...more] = chunk.sectionIds;
        const anchor = first
          ? {
              id: sectionAnchor(first),
              tabIndex: -1,
              'data-focused':
                focusSection && chunk.sectionIds.includes(focusSection) ? 'true' : undefined,
            }
          : {};
        const own =
          chunk.kind === 'plain'
            ? []
            : chunk.indexes.filter((index) => firstChunk.get(index) === chunk.key);
        return (
          <Fragment key={chunk.key}>
            <div
              className="ws-page-chunk"
              data-change={chunk.kind === 'plain' ? undefined : chunk.kind}
              {...anchor}
            >
              {more.map((id) => (
                <span key={id} id={sectionAnchor(id)} tabIndex={-1} className="ws-page-anchor" />
              ))}
              {chunk.kind === 'plain' ? (
                <WorkspaceMarkdown text={chunk.text} />
              ) : chunk.kind === 'marked' ? (
                <WorkspaceMarkdown text={chunk.text} marks={renderer} />
              ) : (
                <BlockParts
                  chunk={chunk}
                  focused={focused}
                  onMarker={showMarkers ? onMarker : null}
                />
              )}
            </div>
            {after && own.length > 0 && after(own)}
          </Fragment>
        );
      })}
    </article>
  );
}
